import { z } from 'zod';
import { Fixture } from './analysis.ts';
import { PositionSchema } from './squad.ts';

/** FPL's single-letter availability code. Mirrors `PlayerSignals['status']`
 * in `apps/api`, which has no schema of its own to import here. */
const StatusSchema = z.enum(['a', 'd', 'i', 's', 'u']);

/**
 * One player in a head-to-head comparison: identity plus the same signals
 * `buildSignals` joins for squad analysis, so "Salah or Saka?" can be
 * answered from real numbers instead of memory. `score` is the existing
 * `scorePlayer` composite, not a new formula — comparison surfaces numbers,
 * it never picks a winner.
 */
export const ComparedPlayer = z.object({
  fplId: z.number().int().positive(),
  name: z.string(),
  position: PositionSchema,
  club: z.string(),
  price: z.number(),
  form: z.number(),
  totalPoints: z.number(),
  seasonMinutes: z.number().int().min(0),
  ictIndex: z.number().optional(),
  expectedGoals: z.number().optional(),
  expectedAssists: z.number().optional(),
  expectedGoalInvolvements: z.number().optional(),
  selectedByPercent: z.number().optional(),
  status: StatusSchema.optional(),
  news: z.string().optional(),
  /** Composite 0-100 ranking score, from the same `scorePlayer` used everywhere else. */
  score: z.number().min(0).max(100),
  nextFixtures: z.array(Fixture).default([]),
  /** Mean difficulty of `nextFixtures`; the neutral value (3) when there are none. */
  fixtureOutlook: z.number(),
});
export type ComparedPlayer = z.infer<typeof ComparedPlayer>;

/** One gameweek's points, for plotting a line chart directly. */
export const ComparisonHistoryPoint = z.object({
  gameweek: z.number().int(),
  points: z.number(),
});
export type ComparisonHistoryPoint = z.infer<typeof ComparisonHistoryPoint>;

/** One compared player's season so far, ascending by gameweek. */
export const PlayerHistory = z.object({
  fplId: z.number().int().positive(),
  series: z.array(ComparisonHistoryPoint),
});
export type PlayerHistory = z.infer<typeof PlayerHistory>;

/**
 * A head-to-head comparison of two to four players. A player whose
 * per-gameweek history couldn't be fetched still appears in `players` and
 * `history` (with an empty `series`) — the table is useful even when the
 * chart isn't, so a history failure is a warning, not a failed comparison.
 */
export const PlayerComparison = z.object({
  players: z.array(ComparedPlayer),
  history: z.array(PlayerHistory),
  warnings: z.array(z.string()).default([]),
});
export type PlayerComparison = z.infer<typeof PlayerComparison>;
